import React, { useEffect, useRef, useState } from 'react';

const QuoteSection = () => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [inView, setInView] = useState(false);

  // Parallax scroll
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const wh = window.innerHeight;
      const progress = (wh / 2 - (rect.top + rect.height / 2)) / wh;
      setOffset(progress * 140);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // In-view trigger
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative w-full min-h-[95vh] overflow-hidden flex items-center justify-center bg-[#0a0a0a]"
    >
      {/* Parallax background image */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translate3d(0, ${offset}px, 0)` }}
      >
        <img
          src="/quote-bg.jpg"
          alt="Bride & Groom"
          className={`w-full h-[125%] object-cover transition-transform duration-[8000ms] ease-out ${
            inView ? 'scale-105' : 'scale-115'
          }`}
          style={{ filter: 'grayscale(20%) contrast(1.05)' }}
        />
        {/* Cinematic gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_25%,_rgba(0,0,0,0.7)_85%)]" />
      </div>

      {/* Moving gold studio glow orbs */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        <div
          className="absolute w-[60vw] h-[60vw] rounded-full opacity-25 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(232,213,168,0.85) 0%, rgba(201,169,110,0.35) 40%, transparent 70%)',
            animation: 'qOrbA 26s ease-in-out infinite',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute w-[45vw] h-[45vw] rounded-full opacity-20 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(255,210,150,0.8) 0%, transparent 75%)',
            animation: 'qOrbB 32s ease-in-out infinite',
            mixBlendMode: 'screen',
          }}
        />
        <div
          className="absolute w-[35vw] h-[35vw] rounded-full opacity-15 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(232,213,168,0.9) 0%, transparent 70%)',
            animation: 'qOrbC 20s ease-in-out infinite',
            mixBlendMode: 'screen',
          }}
        />
      </div>

      {/* Letterbox edges for cinema feel */}
      <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-[#0a0a0a] to-transparent z-[2]" />
      <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#0a0a0a] to-transparent z-[2]" />

      {/* Gold corner ornaments */}
      <span className="hidden md:block absolute top-20 left-16 w-24 h-24 border-t border-l border-[#c9a96e]/70 z-[3]" />
      <span className="hidden md:block absolute top-20 right-16 w-24 h-24 border-t border-r border-[#c9a96e]/70 z-[3]" />
      <span className="hidden md:block absolute bottom-20 left-16 w-24 h-24 border-b border-l border-[#c9a96e]/70 z-[3]" />
      <span className="hidden md:block absolute bottom-20 right-16 w-24 h-24 border-b border-r border-[#c9a96e]/70 z-[3]" />

      {/* Floating gold particles */}
      <div className="absolute inset-0 z-[3] pointer-events-none overflow-hidden">
        {Array.from({ length: 14 }).map((_, i) => {
          const left = (i * 37 + 11) % 100;
          const delay = (i * 0.7) % 6;
          const duration = 10 + (i * 1.3) % 8;
          const size = 1.2 + (i % 3) * 0.9;
          return (
            <span
              key={i}
              className="absolute rounded-full bg-[#e8d5a8]"
              style={{
                left: `${left}%`,
                bottom: '-20px',
                width: `${size}px`,
                height: `${size}px`,
                opacity: 0.5,
                boxShadow: '0 0 8px rgba(232, 213, 168, 0.85)',
                animation: `qFloat ${duration}s linear ${delay}s infinite`,
              }}
            />
          );
        })}
      </div>

      {/* CONTENT */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* EST. 1990 with hairline rule */}
        <div
          className={`flex items-center justify-center gap-5 mb-12 transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="h-px w-14 md:w-20 bg-[#c9a96e]/70" />
          <span className="text-[#c9a96e] text-[11px] md:text-xs tracking-[0.55em] uppercase whitespace-nowrap">
            Est. 1990
          </span>
          <span className="h-px w-14 md:w-20 bg-[#c9a96e]/70" />
        </div>

        {/* Large gold opening quote mark */}
        <div
          className={`font-serif italic text-[#e8c98a] leading-none mb-4 transition-all duration-[1400ms] ${
            inView ? 'opacity-90 scale-100' : 'opacity-0 scale-90'
          }`}
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 'clamp(80px, 14vw, 180px)',
            textShadow:
              '0 0 30px rgba(232,213,168,0.45), 0 0 60px rgba(201,169,110,0.25)',
          }}
        >
          “
        </div>

        {/* The quote */}
        <p
          className={`font-serif italic text-white text-3xl md:text-5xl lg:text-[58px] leading-[1.25] tracking-tight transition-all duration-[1400ms] delay-200 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            textShadow:
              '0 2px 30px rgba(0,0,0,0.6), 0 0 60px rgba(232,213,168,0.08)',
          }}
        >
          You are in charge of the mission.
          <br />
          <span className="text-[#e8c98a]">
            We will implement it for you.
          </span>
        </p>

        {/* Decorative divider */}
        <div
          className={`flex items-center justify-center gap-4 mt-14 transition-all duration-1000 delay-500 ${
            inView ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <span className="h-px w-12 bg-[#c9a96e]/60" />
          <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a96e]" />
          <span className="h-px w-12 bg-[#c9a96e]/60" />
        </div>

        {/* Signature / attribution */}
        <div
          className={`mt-8 transition-all duration-1000 delay-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p
            className="font-serif italic text-[#e8c98a] text-3xl md:text-4xl"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Sir Dion Magee
          </p>
          <p className="text-white/55 text-[10px] tracking-[0.45em] uppercase mt-3">
            Founder &amp; Creative Director
          </p>
        </div>
      </div>

      <style>{`
        @keyframes qOrbA {
          0%, 100% { top: -18%; left: -15%; transform: scale(1); }
          50%      { top: 30%;  left: 35%;  transform: scale(1.15); }
        }
        @keyframes qOrbB {
          0%, 100% { bottom: -20%; right: -10%; transform: scale(1); }
          50%      { bottom: 25%;  right: 30%;  transform: scale(1.2); }
        }
        @keyframes qOrbC {
          0%, 100% { top: 40%; right: 60%; transform: scale(1); }
          50%      { top: 10%; right: 15%; transform: scale(1.25); }
        }
        @keyframes qFloat {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          12%  { opacity: 1; }
          88%  { opacity: 1; }
          100% { transform: translateY(-110vh) translateX(18px); opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default QuoteSection;
